import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../types/kundali';
import { LifeThemeId } from '../types/advancedLifeAnalysis';
import { StructuredRemedyRecord, AssignedRemedyCandidate } from '../types/v517';
import { THEME_PRIMARY_KARAKAS, THEME_PRIMARY_HOUSES } from './fiveRuleHouseEngine';
import { ZODIAC_LORDS, ZODIAC_SIGNS } from './vedicEngine';
import { calculatePlanetTara, getBadhakaHouse } from './vedicGemologyEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.17 — REMEDIES BANK & ASSIGNMENT ENGINE
   Comprehensive Remedial Database, Deterministic Assignment & Safety Rules
   ========================================================================== */

export const STRUCTURED_REMEDIES_BANK: StructuredRemedyRecord[] = [
  // 1. Surya (Sun) Remedies
  {
    id: 'rem_sun_aditya_hridaya',
    name: 'Aditya Hridaya Stotra Recitation',
    nameHi: 'आदित्य हृदय स्तोत्र पाठ',
    category: 'STOTRA',
    planet: 'Sun',
    houseRelevance: [1, 9, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Weak Sun in career/health houses', 'Lack of vitality or confidence'],
    contraindications: ['High fiery constitutional excess (Pitta) without coolant balance'],
    compatibilityNotes: 'Highly compatible with Gayatri mantra and morning meditation.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily once at sunrise',
    duration: '10–12 minutes',
    recommendedTime: 'Sunrise (Brahma Muhurta or Morning)',
    recommendedDay: 'Sunday',
    traditionalBasis: 'Valmiki Ramayana, Sage Agastya formulation',
    entitlementLevel: 'BASIC',
    explanation: 'Reciting Aditya Hridaya Stotra strengthens internal soul-vitality (Atma-Bala), leadership presence, and paternal harmony.',
    explanationHi: 'आदित्य हृदय स्तोत्र का पाठ आत्मबल, नेतृत्व क्षमता व आरोग्य में वृद्धि करता है।'
  },
  {
    id: 'rem_sun_gayatri_japa',
    name: 'Surya Gayatri Mantra (108 repetitions)',
    nameHi: 'सूर्य गायत्री मंत्र (108 जप)',
    category: 'MANTRA',
    planet: 'Sun',
    houseRelevance: [1, 5, 9, 10],
    purpose: 'BALANCING',
    suitableConditions: ['Afflicted Sun, lack of focus, examination clarity'],
    contraindications: ['None; universal spiritual discipline'],
    compatibilityNotes: 'Can be combined with sound frequency 126.22 Hz.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Daily morning',
    duration: '15 minutes',
    recommendedTime: 'Morning (Facing East)',
    recommendedDay: 'Sunday',
    traditionalBasis: 'Rigveda Samhita',
    entitlementLevel: 'BASIC',
    explanation: 'Surya Gayatri harmonizes intellect (Dhiyo Yo Nah Prachodayat) and cleanses subtle nervous channels.',
    explanationHi: 'सूर्य गायत्री बुद्धि को प्रखर करती है एवं अंतःकरण को शुद्ध करती है।'
  },
  {
    id: 'rem_sun_ruby_gemstone',
    name: 'Natural Ruby (Manikya) Gemstone',
    nameHi: 'प्राकृतिक माणिक्य (Ruby) रत्न',
    category: 'GEMSTONE',
    planet: 'Sun',
    houseRelevance: [1, 5, 9, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Sun as functional benefic (Lagna lord/Trikona lord) and well-placed but weak in degrees'],
    contraindications: ['Sun as Lord of 6th, 8th, or 12th house (Libra/Capricorn/Aquarius ascendants)', 'High blood pressure'],
    compatibilityNotes: 'Do not wear alongside Blue Sapphire or Cat’s Eye.',
    priorityScore: 7,
    intensity: 'INTENSIVE',
    frequency: 'Constant wear after consecration',
    duration: 'Lifelong or major Dasha phase',
    recommendedTime: 'Sunday morning in Shukla Paksha',
    recommendedDay: 'Sunday',
    traditionalBasis: 'Brihat Samhita & Garuda Purana',
    entitlementLevel: 'PREMIUM',
    explanation: 'Ruby channels solar infrared rays to amplify personal authority and vitality.',
    explanationHi: 'माणिक्य सूर्य की सकारात्मक ऊर्जा को आकर्षित कर प्रतिष्ठा व शक्ति में वृद्धि करता है।',
    cautionText: 'Never wear Ruby if Sun is a functional malefic or lord of dusthana (6/8/12).',
    cautionTextHi: 'त्रिक भावों के स्वामी सूर्य के लिए माणिक्य कदापि धारण न करें।'
  },
  {
    id: 'rem_sun_daana',
    name: 'Surya Daana — Wheat, Jaggery, Copper & Red Flowers Charity',
    nameHi: 'सूर्य दान — गेहूँ, गुड़, तांबा व लाल पुष्प दान',
    category: 'DANA_CHARITY',
    planet: 'Sun',
    houseRelevance: [1, 6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Sun, pitra dosha indications, high ego friction, eye/vitality strain'],
    contraindications: ['None; sattvic non-possessive charity'],
    compatibilityNotes: 'Perform during noon or sunrise with pure heart.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Sundays or Sankranti days',
    duration: 'Weekly or monthly',
    recommendedTime: 'Noon or Sunrise',
    recommendedDay: 'Sunday',
    traditionalBasis: 'Brihat Parashara Hora Shastra (Upaya Adhyaya)',
    entitlementLevel: 'BASIC',
    explanation: 'Donating wheat, jaggery, red cloth, or copper pacifies afflicted solar energy and resolves paternal karmic debts.',
    explanationHi: 'रविवार को गेहूँ, गुड़, तांबा व लाल वस्त्र का दान सूर्य जनित दोषों का शमन कर पितृ ऋण से मुक्ति दिलाता है।'
  },

  // 2. Chandra (Moon) Remedies
  {
    id: 'rem_moon_om_somaya',
    name: 'Chandra Bija Mantra — Om Shram Shreem Shrom Sah Chandramase Namah',
    nameHi: 'चंद्र बीज मंत्र — ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः',
    category: 'MANTRA',
    planet: 'Moon',
    houseRelevance: [1, 4, 9],
    purpose: 'BALANCING',
    suitableConditions: ['Emotional restlessness, Kemadruma yoga, weak Moon in Scorpio'],
    contraindications: ['None'],
    compatibilityNotes: 'Best chanted while seated peacefully in soft ambient light.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 times daily in evening',
    duration: '10 minutes',
    recommendedTime: 'Evening after sunset or Moonrise',
    recommendedDay: 'Monday',
    traditionalBasis: 'Navagraha Stotram & Agni Purana',
    entitlementLevel: 'BASIC',
    explanation: 'Calms fluctuating mental waves (Chitta Vritti), stabilizes emotional intelligence, and aids restful sleep.',
    explanationHi: 'मानसिक अशांति को शांत कर एकाग्रता व सुखद निद्रा प्रदान करता है।'
  },
  {
    id: 'rem_moon_shiva_panchakshara',
    name: 'Shiva Panchakshara Stotra & Chandra Dhyana',
    nameHi: 'शिव पंचाक्षर स्तोत्र एवं चंद्र ध्यान',
    category: 'STOTRA',
    planet: 'Moon',
    houseRelevance: [1, 4, 9, 12],
    purpose: 'BALANCING',
    suitableConditions: ['Mental anxiety, depression, affliction by Rahu/Saturn, insomnia'],
    contraindications: ['None; universal soothing prayer'],
    compatibilityNotes: 'Complements Monday milk offering or meditation.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily evening or Mondays',
    duration: '10 minutes',
    recommendedTime: 'Evening Sandhya or Bedtime',
    recommendedDay: 'Monday',
    traditionalBasis: 'Adi Shankaracharya & Shiva Purana',
    entitlementLevel: 'BASIC',
    explanation: 'Reciting Shiva Panchakshara Stotra calms the fluctuating lunar mind, enhances serenity, and dissolves melancholy.',
    explanationHi: 'शिव पंचाक्षर स्तोत्र का पाठ चंद्र जनित मानसिक अशांति को शांत कर चित्त में स्थिरता लाता है।'
  },
  {
    id: 'rem_moon_pearl_gemstone',
    name: 'Natural Basra / South Sea Pearl (Moti)',
    nameHi: 'प्राकृतिक मोती (Pearl) रत्न',
    category: 'GEMSTONE',
    planet: 'Moon',
    houseRelevance: [1, 4, 9],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Moon is functional benefic (Cancer/Scorpio/Pisces ascendants) and afflicted by Saturn/Rahu'],
    contraindications: ['Moon as 6th/8th lord for Sagittarius/Aquarius Lagna', 'Chronic phlegmatic congestion'],
    compatibilityNotes: 'Wear in silver on little or ring finger.',
    priorityScore: 7,
    intensity: 'MODERATE',
    frequency: 'Continuous wear',
    duration: 'Dasha period',
    recommendedTime: 'Monday evening during waxing Moon',
    recommendedDay: 'Monday',
    traditionalBasis: 'Jataka Parijata',
    entitlementLevel: 'PREMIUM',
    explanation: 'Pearl radiates calming lunar vibration to soothe emotional volatility and enhance intuition.',
    explanationHi: 'मोती मन की चंचलता को दूर कर शांति व शीतलता प्रदान करता है।'
  },
  {
    id: 'rem_moon_daana',
    name: 'Chandra Daana — White Rice, Milk, Silver & White Sweets Charity',
    nameHi: 'चन्द्र दान — अक्षत (चावल), दूध, मिश्री व सफेद वस्त्र दान',
    category: 'DANA_CHARITY',
    planet: 'Moon',
    houseRelevance: [4, 6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Moon, Kemadruma yoga, maternal health distress, emotional turbulence'],
    contraindications: ['None'],
    compatibilityNotes: 'Offer to elderly motherly women, girls, or temple.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Mondays or Purnima',
    duration: 'Weekly or monthly',
    recommendedTime: 'Evening twilight (Sandhya)',
    recommendedDay: 'Monday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating raw white rice, milk, curd, silver, or white clothes soothes wounded lunar emotions and pacifies distress.',
    explanationHi: 'सोमवार की शाम चावल, दूध, चांदी या सफेद वस्त्र का दान चंद्र दोष का शमन कर मानसिक शांति प्रदान करता है।'
  },

  // 3. Mangal (Mars) Remedies
  {
    id: 'rem_mars_hanuman_chalisa',
    name: 'Sri Hanuman Chalisa & Sundarkand Path',
    nameHi: 'श्री हनुमान चालीसा एवं सुंदरकांड पाठ',
    category: 'STOTRA',
    planet: 'Mars',
    houseRelevance: [1, 3, 6, 10],
    purpose: 'PACIFYING',
    suitableConditions: ['Mangal Dosha, litigation, land disputes, lack of courage or excess anger'],
    contraindications: ['None; universally pacifying and protective'],
    compatibilityNotes: 'Harmonizes well with Tuesday charity.',
    priorityScore: 10,
    intensity: 'GENTLE',
    frequency: '1–3 times daily',
    duration: '10–15 minutes',
    recommendedTime: 'Morning or Twilight (Sandhya)',
    recommendedDay: 'Tuesday / Saturday',
    traditionalBasis: 'Goswami Tulsidas Ramcharitmanas',
    entitlementLevel: 'BASIC',
    explanation: 'Chanting Hanuman Chalisa channels Martian kinetic fire into disciplined focus and overcomes obstacles.',
    explanationHi: 'हनुमान चालीसा का पाठ मंगल दोष का शमन कर निर्भयता व संकल्प शक्ति प्रदान करता है।'
  },
  {
    id: 'rem_mars_beeja_mantra',
    name: 'Mangal Bija Mantra — Om Kraam Kreem Kroum Sah Bhaumaya Namah',
    nameHi: 'मंगल बीज मंत्र — ॐ क्रां क्रीं क्रौं सः भौमाय नमः',
    category: 'MANTRA',
    planet: 'Mars',
    houseRelevance: [1, 3, 6, 10],
    purpose: 'BALANCING',
    suitableConditions: ['Mars afflicted, lack of courage, brotherly discord, lethargy or excessive anger'],
    contraindications: ['None'],
    compatibilityNotes: 'Can be chanted 108 times using red sandalwood or coral rosary.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Tuesdays or daily',
    duration: '12 minutes',
    recommendedTime: 'Morning or Tuesday afternoon',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Navagraha Samhita & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Channels Martian fire and vitality into constructive discipline, physical courage, and obstacle-overcoming focus.',
    explanationHi: 'मंगल बीज मंत्र का 108 जप रक्त विकार, भय, आलस्य व क्रोध को दूर कर शौर्य व आत्मविश्वास में वृद्धि करता है।'
  },
  {
    id: 'rem_mars_coral_gemstone',
    name: 'Natural Red Coral (Moonga) Gemstone',
    nameHi: 'प्राकृतिक लाल मूंगा (Red Coral) रत्न',
    category: 'GEMSTONE',
    planet: 'Mars',
    houseRelevance: [1, 5, 9, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Mars is functional benefic (Aries/Scorpio/Cancer/Leo/Sagittarius/Pisces Lagna)'],
    contraindications: ['Gemini, Virgo, Libra Lagna where Mars is a functional malefic', 'Extreme high blood pressure'],
    compatibilityNotes: 'Wear in copper or gold on ring finger.',
    priorityScore: 7,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear',
    duration: 'Dasha period',
    recommendedTime: 'Tuesday morning in Shukla Paksha',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Garuda Purana & Ratna Shastra',
    entitlementLevel: 'PREMIUM',
    explanation: 'Transmits fiery vital cosmic prana to enhance physical stamina, leadership authority, and swift decisive action.',
    explanationHi: 'प्राकृतिक मूंगा रक्त प्रवाह, साहस एवं कर्मठता को सशक्त करता है।',
    cautionText: 'Avoid wearing Red Coral for Gemini, Virgo, and Libra Lagna where Mars is a strong functional malefic.',
    cautionTextHi: 'मिथुन, कन्या व तुला लग्न वाले जातकों को मूंगा धारण करने से बचना चाहिए।'
  },
  {
    id: 'rem_mars_daana',
    name: 'Mangal Daana — Red Lentils (Masoor Dal), Copper & Jaggery Charity',
    nameHi: 'मंगल दान — मसूर दाल, तांबा, गुड़ व लाल फल दान',
    category: 'DANA_CHARITY',
    planet: 'Mars',
    houseRelevance: [1, 6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Mangal Dosha, aggressive conflicts, blood/bile disorders, surgery prevention'],
    contraindications: ['None'],
    compatibilityNotes: 'Donate to soldiers, physical laborers, or Hanuman temple.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Tuesdays',
    duration: 'Weekly or on Angarki Chaturthi',
    recommendedTime: 'Tuesday afternoon (Madhyahna)',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating red lentils, copper utensils, and sweet jaggery pacifies aggressive Martian fury and shields against injuries.',
    explanationHi: 'मंगलवार को मसूर दाल, तांबा व गुड़ का दान मंगल दोष, रक्त विकार व विवादों का शमन करता है।'
  },

  // 4. Budha (Mercury) Remedies
  {
    id: 'rem_mercury_vishnu_sahasranama',
    name: 'Sri Vishnu Sahasranama Stotram',
    nameHi: 'श्री विष्णु सहस्रनाम स्तोत्र',
    category: 'STOTRA',
    planet: 'Mercury',
    houseRelevance: [2, 4, 5, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Mercury debilitated in Pisces, business stagnation, speech hesitation, analytical exams'],
    contraindications: ['None; supreme classical Vedic remedy'],
    compatibilityNotes: 'Universal spiritual harmony.',
    priorityScore: 10,
    intensity: 'GENTLE',
    frequency: 'Daily once',
    duration: '20–25 minutes',
    recommendedTime: 'Morning',
    recommendedDay: 'Wednesday',
    traditionalBasis: 'Mahabharata, Anushasana Parva',
    entitlementLevel: 'BASIC',
    explanation: 'Clears cognitive pathways, boosts commercial acumen, and blesses speech with persuasiveness.',
    explanationHi: 'व्यापारिक वृद्धि, तीव्र स्मरण शक्ति एवं वाणी में ओजस्विता प्रदायक।'
  },
  {
    id: 'rem_mercury_beeja_mantra',
    name: 'Budha Bija Mantra — Om Braam Breem Broum Sah Budhaya Namah',
    nameHi: 'बुध बीज मंत्र — ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः',
    category: 'MANTRA',
    planet: 'Mercury',
    houseRelevance: [2, 4, 5, 10],
    purpose: 'BALANCING',
    suitableConditions: ['Speech hesitation, nervous agitation, cognitive overload, commercial miscalculations'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant with Tulsi rosary facing North.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Wednesdays or daily',
    duration: '10 minutes',
    recommendedTime: 'Morning (Forenoon)',
    recommendedDay: 'Wednesday',
    traditionalBasis: 'Navagraha Stotram & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Activates the analytical intellect (Buddhi), optimizes neurological communication, and enhances eloquence.',
    explanationHi: 'बुध बीज मंत्र के जप से वाणी में मधुरता, गणितीय तर्कशक्ति एवं व्यापारिक विवेक जागृत होता है।'
  },
  {
    id: 'rem_mercury_emerald_gemstone',
    name: 'Natural Emerald (Panna) Gemstone',
    nameHi: 'प्राकृतिक पन्ना (Emerald) रत्न',
    category: 'GEMSTONE',
    planet: 'Mercury',
    houseRelevance: [1, 2, 5, 9, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Mercury is functional benefic (Taurus/Gemini/Virgo/Libra/Capricorn/Aquarius Lagna)'],
    contraindications: ['Aries, Cancer, Scorpio Lagna where Mercury rules dusthanas (3/6/8/12)'],
    compatibilityNotes: 'Wear in gold or bronze on little finger.',
    priorityScore: 8,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear',
    duration: 'Dasha or academic period',
    recommendedTime: 'Wednesday morning in Shukla Paksha',
    recommendedDay: 'Wednesday',
    traditionalBasis: 'Garuda Purana',
    entitlementLevel: 'PREMIUM',
    explanation: 'Emerald amplifies intellectual discernment, data analysis, commercial negotiation, and nervous stability.',
    explanationHi: 'पन्ना धारण करने से एकाग्रता, व्यापारिक सफलता व संवाद कौशल में आशातीत वृद्धि होती है।',
    cautionText: 'Never wear Emerald if Mercury is a strong functional malefic (Aries/Cancer/Scorpio Lagna).',
    cautionTextHi: 'मेष, कर्क व वृश्चिक लग्न वाले जातकों को पन्ना धारण नहीं करना चाहिए।'
  },
  {
    id: 'rem_mercury_daana',
    name: 'Budha Daana — Whole Moong Dal, Green Cloth & Cow Fodder (Gau-Seva)',
    nameHi: 'बुध दान — साबुत मूंग दाल, हरे वस्त्र, पुस्तकें व गो-ग्रास',
    category: 'DANA_CHARITY',
    planet: 'Mercury',
    houseRelevance: [2, 6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Mercury, skin ailments, speech defects, financial fraud victims'],
    contraindications: ['None'],
    compatibilityNotes: 'Feed green grass (palak/fodder) to cows or donate stationery to students.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Wednesdays',
    duration: 'Weekly',
    recommendedTime: 'Wednesday morning',
    recommendedDay: 'Wednesday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating green moong dal, educational books, or green grass to sacred cows clears karmic intellectual obstacles.',
    explanationHi: 'बुधवार को गाय को हरा चारा खिलाने एवं मूंग दाल व पुस्तकों का दान करने से बुध के अनिष्ट फलों का शमन होता है।'
  },

  // 5. Guru (Jupiter) Remedies
  {
    id: 'rem_jupiter_guru_mantra',
    name: 'Brihaspati Bija Mantra (Om Gram Greem Grom Sah Gurave Namah)',
    nameHi: 'बृहस्पति बीज मंत्र (ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः)',
    category: 'MANTRA',
    planet: 'Jupiter',
    houseRelevance: [1, 2, 5, 9, 11],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Jupiter weak, progeny delay, financial wisdom, higher spiritual learning'],
    contraindications: ['None'],
    compatibilityNotes: 'Combine with yellow apparel and Thursday fasting where suitable.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions',
    duration: '15 minutes',
    recommendedTime: 'Morning facing North-East (Ishanya)',
    recommendedDay: 'Thursday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Expands divine grace, righteous prosperity (Dharma-Artha), and philosophical clarity.',
    explanationHi: 'ज्ञान, धन एवं संतान सुख में वृद्धि तथा गुरु कृपा की प्राप्ति।'
  },
  {
    id: 'rem_jupiter_guru_stotra',
    name: 'Sri Brihaspati Stotram & Guru Paduka Stotram',
    nameHi: 'श्री बृहस्पति स्तोत्र एवं गुरु पादुका स्तोत्र',
    category: 'STOTRA',
    planet: 'Jupiter',
    houseRelevance: [1, 2, 5, 9, 11],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Jupiter weak in Shadbala, lack of guidance, spiritual disillusionment, progeny hurdles'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant with yellow flowers and dhoop on Thursday.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily or Thursdays',
    duration: '10 minutes',
    recommendedTime: 'Morning facing North-East',
    recommendedDay: 'Thursday',
    traditionalBasis: 'Skanda Purana & Adi Shankaracharya',
    entitlementLevel: 'BASIC',
    explanation: 'Invokes divine grace of the Preceptor to bestow wisdom, righteous judgment, moral strength, and spiritual elevation.',
    explanationHi: 'गुरु पादुका स्तोत्र से गुरु कृपा, सद्बुद्धि एवं आध्यात्मिक प्रकाश की प्राप्ति होती है।'
  },
  {
    id: 'rem_jupiter_yellow_sapphire',
    name: 'Natural Yellow Sapphire (Pukhraj)',
    nameHi: 'प्राकृतिक पुखराज (Yellow Sapphire)',
    category: 'GEMSTONE',
    planet: 'Jupiter',
    houseRelevance: [1, 2, 5, 9, 11],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Jupiter is functional benefic (Aries/Leo/Scorpio/Sagittarius/Pisces Lagna)'],
    contraindications: ['Taurus, Libra, and Gemini Lagna where Jupiter rules 6th/8th/11th dusthanas'],
    compatibilityNotes: 'Wear in Gold or Panchdhatu on Index Finger.',
    priorityScore: 8,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear',
    duration: 'Lifelong',
    recommendedTime: 'Thursday morning in Pushya Nakshatra or Shukla Paksha',
    recommendedDay: 'Thursday',
    traditionalBasis: 'Garuda Purana & Ratna Shastra',
    entitlementLevel: 'PREMIUM',
    explanation: 'Transmits golden cosmic wavelength of Jupiter to maximize expansion, luck, and wisdom.',
    explanationHi: 'पुखराज धारण करने से भाग्य वृद्धि, विद्या लाभ एवं मान-सम्मान में विस्तार होता है।',
    cautionText: 'Avoid wearing Yellow Sapphire for Taurus/Libra Lagna where Jupiter is a strong functional malefic.',
    cautionTextHi: 'वृषभ व तुला लग्न वाले पुखराज धारण करने से बचें।'
  },
  {
    id: 'rem_jupiter_daana',
    name: 'Brihaspati Daana — Chana Dal, Turmeric, Yellow Clothes & Religious Books',
    nameHi: 'बृहस्पति दान — चना दाल, हल्दी, पीले वस्त्र व धार्मिक ग्रंथ दान',
    category: 'DANA_CHARITY',
    planet: 'Jupiter',
    houseRelevance: [6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Jupiter, liver/digestive disorders, lack of progeny, vanity/excess pride'],
    contraindications: ['None'],
    compatibilityNotes: 'Donate to teachers, scholars, or temple priests on Thursday.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Thursdays',
    duration: 'Weekly',
    recommendedTime: 'Thursday morning in Guru Hora',
    recommendedDay: 'Thursday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating chana dal, turmeric, yellow sweets, and spiritual texts balances Jupiterian excess and invokes divine benevolence.',
    explanationHi: 'गुरुवार को चना दाल, हल्दी व पीले वस्त्रों का दान गुरु जनित दोषों का शमन कर भाग्य का द्वार खोलता है।'
  },

  // 6. Shukra (Venus) Remedies
  {
    id: 'rem_venus_kanakadhara',
    name: 'Sri Kanakadhara Stotram & Mahalakshmi Japa',
    nameHi: 'श्री कनकधारा स्तोत्र एवं महालक्ष्मी जप',
    category: 'STOTRA',
    planet: 'Venus',
    houseRelevance: [2, 4, 7, 11, 12],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Financial abundance, marriage harmony, creative aesthetic refinement'],
    contraindications: ['None'],
    compatibilityNotes: 'Complements fragrant white flowers and Friday meditation.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily once or Fridays',
    duration: '10 minutes',
    recommendedTime: 'Morning or Evening Sandhya',
    recommendedDay: 'Friday',
    traditionalBasis: 'Adi Shankaracharya Composition',
    entitlementLevel: 'BASIC',
    explanation: 'Invokes Mahalakshmi divine grace for continuous prosperity, domestic peace, and artistic fulfillment.',
    explanationHi: 'कनकधारा स्तोत्र से ऐश्वर्य, वैवाहिक सौहार्द एवं आर्थिक समृद्धि की प्राप्ति होती है।'
  },
  {
    id: 'rem_venus_beeja_mantra',
    name: 'Shukra Bija Mantra — Om Draam Dreem Droum Sah Shukraya Namah',
    nameHi: 'शुक्र बीज मंत्र — ॐ द्रां द्रीं द्रौं सः शुक्राय नमः',
    category: 'MANTRA',
    planet: 'Venus',
    houseRelevance: [2, 4, 7, 11, 12],
    purpose: 'BALANCING',
    suitableConditions: ['Venus afflicted, marital friction, lack of artistic inspiration, reproductive weakness'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant with crystal or white quartz rosary.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Fridays or daily',
    duration: '12 minutes',
    recommendedTime: 'Morning or Evening Sandhya',
    recommendedDay: 'Friday',
    traditionalBasis: 'Navagraha Samhita & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Harmonizes Shukra cosmic vibrations to cultivate aesthetic refinement, mutual romantic devotion, and graceful prosperity.',
    explanationHi: 'शुक्र बीज मंत्र से आकर्षण, दांपत्य सुख, सौंदर्य एवं भौतिक व आत्मिक ऐश्वर्य की वृद्धि होती है।'
  },
  {
    id: 'rem_venus_diamond_gemstone',
    name: 'Natural Diamond / White Zircon (Heera / Sphatik)',
    nameHi: 'प्राकृतिक हीरा / श्वेत जरकन (Diamond / White Zircon)',
    category: 'GEMSTONE',
    planet: 'Venus',
    houseRelevance: [1, 2, 7, 9, 10],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Venus is functional benefic (Taurus/Gemini/Virgo/Libra/Capricorn/Aquarius Lagna)'],
    contraindications: ['Aries, Cancer, Leo, Scorpio Lagna where Venus is a functional malefic (2/7/3/8/12 lord)'],
    compatibilityNotes: 'Wear in platinum, white gold, or silver on middle or little finger.',
    priorityScore: 7,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear',
    duration: 'Dasha period',
    recommendedTime: 'Friday morning in Shukla Paksha',
    recommendedDay: 'Friday',
    traditionalBasis: 'Garuda Purana & Agni Purana',
    entitlementLevel: 'PREMIUM',
    explanation: 'Channels pure white aesthetic and creative brilliance to expand gracious luxury, romantic bonding, and artistic charisma.',
    explanationHi: 'हीरा या जरकन धारण करने से कलात्मक प्रतिभा, सौन्दर्य व वैवाहिक जीवन में मधुरता आती है।',
    cautionText: 'Avoid wearing Diamond if Venus is a functional malefic (Aries/Leo/Scorpio/Cancer Lagna).',
    cautionTextHi: 'मेष, सिंह, वृश्चिक व कर्क लग्न वाले जातकों को हीरा धारण करने से बचना चाहिए।'
  },
  {
    id: 'rem_venus_daana',
    name: 'Shukra Daana — White Silk, Pure Cow Ghee, Curd, Sugar & Perfumes',
    nameHi: 'शुक्र दान — श्वेत रेशमी वस्त्र, शुद्ध घी, मिश्री, कपूर व इत्र दान',
    category: 'DANA_CHARITY',
    planet: 'Venus',
    houseRelevance: [6, 7, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Venus, severe marital strife, diabetes/kidney strain, sensual addictions'],
    contraindications: ['None'],
    compatibilityNotes: 'Gift to married women (suhagan), young girls, or temple.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Fridays',
    duration: 'Weekly',
    recommendedTime: 'Friday morning or twilight',
    recommendedDay: 'Friday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating white silk, pure cow ghee, sugar sweets, and fragrant natural ittar pacifies marital and sensual karmic friction.',
    explanationHi: 'शुक्रवार को सुहागिन महिलाओं को सफेद वस्त्र, शुद्ध घी व इत्र का दान करने से वैवाहिक कलह समाप्त होती है।'
  },

  // 7. Shani (Saturn) Remedies
  {
    id: 'rem_saturn_shani_seva',
    name: 'Seva to Elderly, Disabled & Laborers (Shani Seva)',
    nameHi: 'वृद्ध, असहाय व श्रमिकों की सेवा एवं दान',
    category: 'SEVA_SERVICE',
    planet: 'Saturn',
    houseRelevance: [6, 8, 10, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Sade Sati, Dhaiya, Saturn Dasha, career delays, joint aches'],
    contraindications: ['None; premier non-material karma yoga remedy'],
    compatibilityNotes: 'Pair with feeding black sesame or mustard oil lamps on Saturday.',
    priorityScore: 10,
    intensity: 'GENTLE',
    frequency: 'Every Saturday or weekly service',
    duration: 'Ongoing lifestyle practice',
    recommendedTime: 'Saturday Evening',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Brihat Parashara Karma Vipaka',
    entitlementLevel: 'BASIC',
    explanation: 'Direct karmic propitiation of Saturn through humility, ethical labor, and selfless charity.',
    explanationHi: 'निःस्वार्थ सेवा व दान से शनिदेव की कृपा व साढ़े साती के कष्टों से मुक्ति मिलती है।'
  },
  {
    id: 'rem_saturn_dasharatha_stotra',
    name: 'King Dasharatha Shani Stotra Recitation',
    nameHi: 'दशरथ कृत शनि स्तोत्र पाठ',
    category: 'STOTRA',
    planet: 'Saturn',
    houseRelevance: [6, 8, 10, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Sade Sati, Saturn Kantaka or Ashtama Shani, severe hurdles and delays'],
    contraindications: ['None; classical pacifying stotra'],
    compatibilityNotes: 'Light a mustard oil lamp before recitation on Saturday evenings.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Saturday evenings or daily',
    duration: '10 minutes',
    recommendedTime: 'Saturday Sunset or Evening',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Padma Purana & King Dasharatha Formulation',
    entitlementLevel: 'BASIC',
    explanation: 'Reciting King Dasharatha Shani Stotra pacifies the severe testing of Saturn and bestows patience, perseverance, and relief.',
    explanationHi: 'दशरथ कृत शनि स्तोत्र के पाठ से शनि की साढ़े साती व ढैय्या के कष्टों से राहत मिलती है।'
  },
  {
    id: 'rem_saturn_mahamrityunjaya',
    name: 'Maha Mrityunjaya Mantra (108 Japa)',
    nameHi: 'महामृत्युंजय मंत्र (108 जप)',
    category: 'JAPA',
    planet: 'Saturn',
    houseRelevance: [1, 6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Severe Sade Sati, chronic health issues, fear of longevity, Maraka periods'],
    contraindications: ['None; supreme life-giving shield'],
    compatibilityNotes: 'Can be combined with 528 Hz / 147.85 Hz sound meditation.',
    priorityScore: 10,
    intensity: 'MODERATE',
    frequency: 'Daily morning with water offering',
    duration: '15–20 minutes',
    recommendedTime: 'Brahma Muhurta or Morning',
    recommendedDay: 'Monday / Saturday',
    traditionalBasis: 'Rigveda & Shiva Purana',
    entitlementLevel: 'BASIC',
    explanation: 'Conquers existential fear, provides longevity shield (Ayu-Raksha), and purifies deep cellular vitality.',
    explanationHi: 'आरोग्य रक्षा, भय मुक्ति एवं दीर्घायु हेतु परम कल्याणकारी मंत्र।'
  },
  {
    id: 'rem_saturn_beeja_mantra',
    name: 'Shani Bija Mantra — Om Praam Preem Proum Sah Shanishcharaya Namah',
    nameHi: 'शनि बीज मंत्र — ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः',
    category: 'MANTRA',
    planet: 'Saturn',
    houseRelevance: [6, 8, 10, 11, 12],
    purpose: 'BALANCING',
    suitableConditions: ['Sade Sati, Dhaiya, chronic procrastination, knee/joint stiffness, depressive melancholy'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant 108 times facing West on Saturday evenings.',
    priorityScore: 10,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Saturdays or daily',
    duration: '15 minutes',
    recommendedTime: 'Saturday evening after sunset',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Navagraha Samhita & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Humbleness, patience, and karmic endurance are bestowed by Shani Bija Mantra, dissolving deep karmic resistance.',
    explanationHi: 'शनि बीज मंत्र का जप साढ़े साती के कष्टों, आलस्य व मानसिक अवसाद को दूर कर धैर्य व अनुशासन प्रदान करता है।'
  },
  {
    id: 'rem_saturn_blue_sapphire',
    name: 'Natural Blue Sapphire (Neelam) Gemstone',
    nameHi: 'प्राकृतिक नीलम (Blue Sapphire) रत्न',
    category: 'GEMSTONE',
    planet: 'Saturn',
    houseRelevance: [1, 5, 9, 10, 11],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Saturn is strong Yogakaraka or functional benefic (Taurus/Libra/Capricorn/Aquarius Lagna)'],
    contraindications: ['Aries, Cancer, Leo, Scorpio Lagna where Saturn is an inimical malefic', 'Never wear without 3-day trial'],
    compatibilityNotes: 'Wear in Panchdhatu, white gold or iron on middle finger.',
    priorityScore: 8,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear after trial test',
    duration: 'Dasha period',
    recommendedTime: 'Saturday evening in Shukla Paksha',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Garuda Purana & Ratna Shastra',
    entitlementLevel: 'PREMIUM',
    explanation: 'Channels high-energy cosmic blue ray of Saturn to bestow monumental endurance, structural focus, and institutional power.',
    explanationHi: 'नीलम शनि की शक्ति का संवाहक है जो कर्म क्षेत्र में अप्रत्याशित उन्नति व एकाग्रता प्रदान करता है।',
    cautionText: 'Contraindication: Strictly avoid for Aries, Cancer, Leo, Scorpio Lagna. Must undergo a 3-day test trial under pillow.',
    cautionTextHi: 'मेष, कर्क, सिंह व वृश्चिक लग्न वाले नीलम कदापि न पहनें। ३ दिन का परीक्षण अनिवार्य है।'
  },
  {
    id: 'rem_saturn_daana',
    name: 'Shani Daana — Mustard Oil (Chhaya Daan), Black Sesame, Urad Dal & Iron Utensils',
    nameHi: 'शनि दान — सरसों तेल (छाया दान), काले तिल, उड़द दाल, काले वस्त्र व लोहा दान',
    category: 'DANA_CHARITY',
    planet: 'Saturn',
    houseRelevance: [6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Sade Sati, Saturn transit over natal Moon, acute joint pains, delayed career'],
    contraindications: ['None; supreme non-material and material karmic cleansing'],
    compatibilityNotes: 'Donate to sweepers, laborers, or disabled persons on Saturday.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Saturdays',
    duration: 'Weekly during Sade Sati',
    recommendedTime: 'Saturday sunset (Sandhya)',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Performing Chhaya Daan with mustard oil and donating black sesame seeds dissolves heavy Saturnine karmic burdens.',
    explanationHi: 'शनिवार शाम सरसों के तेल में अपना मुख देखकर दान (छाया दान) करने से शनि की पीड़ा व रुकावटों से मुक्ति मिलती है।'
  },

  // 8. Rahu & Ketu Remedies
  {
    id: 'rem_rahu_durga_saptashati',
    name: 'Durga Saptashati / Argala Stotram & Rahu Pacification',
    nameHi: 'दुर्गा सप्तशती / अर्गला स्तोत्र पाठ',
    category: 'STOTRA',
    planet: 'Rahu',
    houseRelevance: [6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Rahu Dasha, anxiety, sudden volatility, illusionary fears'],
    contraindications: ['None'],
    compatibilityNotes: 'Pair with feeding stray animals and birds.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily or on Rahu Kaal / Wednesday evening',
    duration: '12 minutes',
    recommendedTime: 'Evening',
    recommendedDay: 'Wednesday / Saturday',
    traditionalBasis: 'Markandeya Purana',
    entitlementLevel: 'BASIC',
    explanation: 'Mother Durga energy dissolves Rahu illusions, shields against psychic turmoil, and grounds perception.',
    explanationHi: 'दुर्गा स्तुति से राहु के भ्रम व भय का नाश होकर मानसिक स्पष्टता प्राप्त होती है।'
  },
  {
    id: 'rem_rahu_beeja_mantra',
    name: 'Rahu Bija Mantra — Om Bhraam Bhreem Bhroum Sah Rahave Namah',
    nameHi: 'राहु बीज मंत्र — ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः',
    category: 'MANTRA',
    planet: 'Rahu',
    houseRelevance: [3, 6, 10, 11],
    purpose: 'BALANCING',
    suitableConditions: ['Rahu Dasha/Antardasha, obsessive anxiety, phobias, sleep paralysis, sudden disorientation'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant during Rahu Kaal or Wednesday/Saturday night.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Saturdays or daily',
    duration: '15 minutes',
    recommendedTime: 'Night or Rahu Kaal',
    recommendedDay: 'Saturday / Wednesday',
    traditionalBasis: 'Navagraha Samhita & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Penetrates through cognitive fog and illusions, grounding erratic nervous energy and shielding subtle perception.',
    explanationHi: 'राहु बीज मंत्र के जप से भ्रम, भय, अनिद्रा व आकस्मिक संकटों का निवारण होकर अंतःप्रज्ञा जाग्रत होती है।'
  },
  {
    id: 'rem_rahu_hessonite_gemstone',
    name: 'Natural Hessonite Garnet (Gomed) Gemstone',
    nameHi: 'प्राकृतिक गोमेद (Hessonite Garnet) रत्न',
    category: 'GEMSTONE',
    planet: 'Rahu',
    houseRelevance: [3, 6, 10, 11],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Rahu well placed in Upachaya houses (3, 6, 10, 11) for Taurus/Gemini/Virgo/Libra/Aquarius Lagna'],
    contraindications: ['Aries, Cancer, Leo, Sagittarius, Pisces Lagna; do not wear if placed in 8th or 12th house'],
    compatibilityNotes: 'Wear in silver or Ashtadhatu on middle finger.',
    priorityScore: 6,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear after trial',
    duration: 'Rahu Dasha period',
    recommendedTime: 'Saturday night in Shukla Paksha',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Garuda Purana',
    entitlementLevel: 'PREMIUM',
    explanation: 'Focuses ultraviolet cosmic rays to accelerate political, technological, and worldly breakthroughs.',
    explanationHi: 'गोमेद धारण करने से राजनीति, तकनीक व विदेशी संपर्कों में अप्रत्याशित सफलता मिलती है।',
    cautionText: 'Contraindication: Never wear Hessonite if Rahu is placed in 1st, 5th, 8th, 9th, or 12th house without expert scrutiny.',
    cautionTextHi: 'त्रिक अथवा त्रिकोण भावों में स्थित राहु हेतु गोमेद धारण कदापि न करें।'
  },
  {
    id: 'rem_rahu_daana',
    name: 'Rahu Daana — Multi-colored Blanket, Black Mustard, Dry Coconut & Radish',
    nameHi: 'राहु दान — कम्बल, राई, नारियल, उड़द व मूली दान',
    category: 'DANA_CHARITY',
    planet: 'Rahu',
    houseRelevance: [6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Kalsarp Dosha, severe Rahu Mahadasha distress, sudden financial losses, chronic skin phobias'],
    contraindications: ['None'],
    compatibilityNotes: 'Offer dry coconut to flowing river or donate dark blankets to homeless.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Saturdays or Amavasya',
    duration: 'Weekly or monthly',
    recommendedTime: 'Saturday evening or Rahu Kaal',
    recommendedDay: 'Saturday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating dark blankets, mustard seeds, and immersing dry coconut in flowing water deflects erratic Rahu turmoil.',
    explanationHi: 'शनिवार शाम कंबल, उड़द व नारियल का दान राहु के भ्रम, अनिष्ट व कालसर्प पीड़ा को शांत करता है।'
  },
  {
    id: 'rem_ketu_ganesha_athatvashirsha',
    name: 'Sri Ganapati Atharvashirsha Recitation',
    nameHi: 'श्री गणपति अथर्वशीर्ष पाठ',
    category: 'STOTRA',
    planet: 'Ketu',
    houseRelevance: [8, 9, 12],
    purpose: 'BALANCING',
    suitableConditions: ['Ketu Dasha, spiritual awakening, overcoming deep blockages'],
    contraindications: ['None'],
    compatibilityNotes: 'Offering Durva grass to Lord Ganesha on Wednesdays/Sankashti.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: 'Daily once',
    duration: '8 minutes',
    recommendedTime: 'Morning',
    recommendedDay: 'Wednesday / Chaturthi',
    traditionalBasis: 'Atharvaveda Upanishad',
    entitlementLevel: 'BASIC',
    explanation: 'Lord Ganesha as supreme master of Ketu removes spiritual obstacles and aligns the subtle intellect.',
    explanationHi: 'गणेश अथर्वशीर्ष के पाठ से केतु जनित विघ्नों का निवारण एवं आत्म-ज्ञान की प्राप्ति होती है।'
  },
  {
    id: 'rem_ketu_beeja_mantra',
    name: 'Ketu Bija Mantra — Om Sraam Sreem Sroum Sah Ketave Namah',
    nameHi: 'केतु बीज मंत्र — ॐ स्रां स्रीं स्रौं सः केतवे नमः',
    category: 'MANTRA',
    planet: 'Ketu',
    houseRelevance: [8, 9, 12],
    purpose: 'BALANCING',
    suitableConditions: ['Ketu Dasha, spiritual isolation, enigmatic health diagnoses, fear of unknown'],
    contraindications: ['None'],
    compatibilityNotes: 'Chant 108 times using Rudraksha or Cat’s Eye rosary.',
    priorityScore: 9,
    intensity: 'GENTLE',
    frequency: '108 repetitions on Tuesdays or daily',
    duration: '10 minutes',
    recommendedTime: 'Morning or Sunset',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Navagraha Samhita & BPHS',
    entitlementLevel: 'BASIC',
    explanation: 'Awakens Moksha-oriented spiritual discrimination (Viveka), clears subconscious karmic blockages, and heals enigmatic ailments.',
    explanationHi: 'केतु बीज मंत्र के जप से आत्म-साक्षात्कार, गुप्त विद्या का ज्ञान एवं अज्ञात भय व व्याधियों से मुक्ति मिलती है।'
  },
  {
    id: 'rem_ketu_cats_eye_gemstone',
    name: 'Natural Cat’s Eye (Lehsuniya / Vaidurya) Gemstone',
    nameHi: 'प्राकृतिक लहसुनिया (Cat’s Eye) रत्न',
    category: 'GEMSTONE',
    planet: 'Ketu',
    houseRelevance: [3, 6, 9, 12],
    purpose: 'STRENGTHENING',
    suitableConditions: ['Ketu well placed in spiritual or Upachaya houses for Pisces, Sagittarius, Aries Lagna'],
    contraindications: ['Do not wear alongside Ruby, Pearl, or Coral; strictly avoid if placed in 2nd, 7th, or 8th house'],
    compatibilityNotes: 'Wear in silver or Panchdhatu on middle or little finger.',
    priorityScore: 6,
    intensity: 'INTENSIVE',
    frequency: 'Continuous wear after trial',
    duration: 'Ketu Dasha period',
    recommendedTime: 'Tuesday night in Shukla Paksha',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Garuda Purana',
    entitlementLevel: 'PREMIUM',
    explanation: 'Infuses subtle infrared chrysoberyl chatoyancy to sharpen intuitive sixth sense, occult mastery, and rapid spiritual release.',
    explanationHi: 'लहसुनिया धारण करने से गुप्त ज्ञान, इंट्यूशन एवं आध्यात्मिक पथ पर तीव्र प्रगति होती है।',
    cautionText: 'Contraindication: Must be tested for 3 days. Never wear if Ketu is placed in Maraka (2/7) houses.',
    cautionTextHi: 'मारक भावों (२/७) में स्थित केतु हेतु लहसुनिया कभी न पहनें।'
  },
  {
    id: 'rem_ketu_daana',
    name: 'Ketu Daana — Seven Grains (Sapta-Dhanya), Sesame, Dual-Tone Blanket & Feeding Dogs',
    nameHi: 'केतु दान — सप्तधान्य, तिल, दो-रंगी कम्बल व श्वान (कुत्तों) को भोजन',
    category: 'DANA_CHARITY',
    planet: 'Ketu',
    houseRelevance: [6, 8, 12],
    purpose: 'PACIFYING',
    suitableConditions: ['Afflicted Ketu, chronic skin allergies, spiritual confusion, mysterious debts'],
    contraindications: ['None'],
    compatibilityNotes: 'Feed sweet rotis to stray street dogs (especially black/white dogs) on Tuesdays.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Tuesdays / Sankashti',
    duration: 'Weekly',
    recommendedTime: 'Sunset or late evening',
    recommendedDay: 'Tuesday',
    traditionalBasis: 'Brihat Parashara Hora Shastra',
    entitlementLevel: 'BASIC',
    explanation: 'Donating seven grains, two-toned blankets, and regularly feeding street dogs dispels Ketu karmic afflictions and enigmatic blocks.',
    explanationHi: 'सप्तधान्य का दान तथा दोरंगे कुत्तों को रोटी खिलाने से केतु के समस्त अनिष्ट शांत होते हैं।'
  },

  // 10. Sound & Frequency Remedies
  {
    id: 'rem_sound_planetary_harmony',
    name: 'Vedic Planetary Harmonic Resonance Audio',
    nameHi: 'वैदिक नवग्रह ध्वनि अनुनाद (Sound Remedy)',
    category: 'FREQUENCY_SOUND',
    planet: 'GENERAL',
    purpose: 'BALANCING',
    suitableConditions: ['General stress, meditation support, chakra balancing'],
    contraindications: ['Do not use while driving or operating heavy machinery'],
    compatibilityNotes: 'Play alongside mantra japa or during quiet evening relaxation.',
    priorityScore: 8,
    intensity: 'GENTLE',
    frequency: 'Daily 10–20 minutes',
    duration: '15 minutes',
    recommendedTime: 'Evening / Bedtime / Meditation',
    recommendedDay: 'Daily',
    traditionalBasis: 'Nada Yoga & Classical Vedic Acoustics',
    entitlementLevel: 'BASIC',
    explanation: 'Combines precise planetary frequencies with solfeggio undertones to restore subtle nervous homeostasis.',
    explanationHi: 'ध्वनि तरंगें मानसिक तनाव को कम कर आंतरिक शांति व संतुलन स्थापित करती हैं।',
    frequencyHz: 432,
    solfeggioHz: 528
  }
];

export const PURUSHARTHA_SAFEGUARD = {
  en: {
    title: 'Classical Purushartha Safeguard',
    text: 'Vedic remedies (Upayas) are sacred tools for subtle karmic harmonization (Daiva). They operate in union with conscious personal effort (Purushartha) and ethical conduct. Remedies are not transactional guarantees and never substitute for medical, legal, or psychological care.'
  },
  hi: {
    title: 'शास्त्रीय पुरुषार्थ मर्यादा',
    text: 'वैदिक उपाय कर्मिक व सूक्ष्म ऊर्जा संतुलन (दैव) के साधन हैं। ये व्यक्तिगत पुरुषार्थ (सचेत कर्म) एवं नैतिक आचरण के साथ ही फलित होते हैं। उपाय कोई जादुई चमत्कार या निश्चित गारंटी नहीं हैं और न ही चिकित्सकीय अथवा कानूनी परामर्श का विकल्प हैं।'
  }
};

/* ==========================================================================
   CLASSICAL PARASHARI LORDSHIP & FUNCTIONAL CLASSIFICATION (BPHS CH. 34)
   ========================================================================== */

export interface ParashariLordship {
  planet: PlanetName;
  lagnaSign: ZodiacSign;
  ruledHouses: number[];
  isFunctionalBenefic: boolean;
  isFunctionalMalefic: boolean;
  isYogakaraka: boolean;
  isMaraka: boolean;
  isPureDusthanaLord: boolean;
  isBadhakaLord: boolean;
  isLagnaLord: boolean;
  isTrikonaLord: boolean;
  isKendraLord: boolean;
  nature: 'Benefic' | 'Malefic' | 'Yogakaraka' | 'Neutral' | 'Maraka';
  reason: string;
}

export const FUNCTIONAL_MALEFICS_BY_LAGNA: Record<ZodiacSign, PlanetName[]> = {
  Aries: ['Mercury', 'Saturn', 'Venus'],
  Taurus: ['Jupiter', 'Moon', 'Mars'],
  Gemini: ['Mars', 'Jupiter', 'Sun'],
  Cancer: ['Mercury', 'Venus', 'Saturn'],
  Leo: ['Mercury', 'Venus', 'Saturn'],
  Virgo: ['Mars', 'Jupiter', 'Moon'],
  Libra: ['Jupiter', 'Sun', 'Mars'],
  Scorpio: ['Mercury', 'Venus'],
  Sagittarius: ['Venus', 'Mercury'],
  Capricorn: ['Mars', 'Jupiter', 'Moon'],
  Aquarius: ['Jupiter', 'Moon', 'Mars'],
  Pisces: ['Sun', 'Venus', 'Saturn', 'Mercury']
};

export const FUNCTIONAL_BENEFICS_BY_LAGNA: Record<ZodiacSign, PlanetName[]> = {
  Aries: ['Sun', 'Jupiter', 'Mars'],
  Taurus: ['Saturn', 'Venus', 'Mercury'],
  Gemini: ['Venus', 'Mercury'],
  Cancer: ['Mars', 'Moon', 'Jupiter'],
  Leo: ['Mars', 'Sun', 'Jupiter'],
  Virgo: ['Venus', 'Mercury'],
  Libra: ['Saturn', 'Venus', 'Mercury'],
  Scorpio: ['Jupiter', 'Sun', 'Mars', 'Moon'],
  Sagittarius: ['Sun', 'Mars', 'Jupiter'],
  Capricorn: ['Venus', 'Saturn', 'Mercury'],
  Aquarius: ['Venus', 'Saturn'],
  Pisces: ['Moon', 'Jupiter', 'Mars']
};

export const YOGAKARAKAS_BY_LAGNA: Record<ZodiacSign, PlanetName | undefined> = {
  Aries: undefined,
  Taurus: 'Saturn',
  Gemini: undefined,
  Cancer: 'Mars',
  Leo: 'Mars',
  Virgo: undefined,
  Libra: 'Saturn',
  Scorpio: undefined,
  Sagittarius: undefined,
  Capricorn: 'Venus',
  Aquarius: 'Venus',
  Pisces: undefined
};

export function evaluateParashariLordship(
  planet: PlanetName,
  lagnaSign: ZodiacSign
): ParashariLordship {
  const lagnaIdx = ZODIAC_SIGNS.indexOf(lagnaSign);
  const ruledHouses: number[] = [];

  if (lagnaIdx >= 0) {
    for (let h = 1; h <= 12; h++) {
      const signIdx = (lagnaIdx + h - 1) % 12;
      const signName = ZODIAC_SIGNS[signIdx];
      if (ZODIAC_LORDS[signName] === planet) {
        ruledHouses.push(h);
      }
    }
  }

  const isLagnaLord = ruledHouses.includes(1);
  const isTrikonaLord = ruledHouses.some(h => [1, 5, 9].includes(h));
  const isKendraLord = ruledHouses.some(h => [1, 4, 7, 10].includes(h));
  const isYogakaraka = YOGAKARAKAS_BY_LAGNA[lagnaSign] === planet ||
    (ruledHouses.some(h => [4, 7, 10].includes(h)) && ruledHouses.some(h => [5, 9].includes(h)));

  const isPureDusthanaLord = (ruledHouses.includes(6) || ruledHouses.includes(8) || ruledHouses.includes(12)) &&
    !isLagnaLord && !isYogakaraka && !ruledHouses.some(h => [1, 5, 9].includes(h));

  const isMaraka = ruledHouses.includes(2) || ruledHouses.includes(7);
  const badhakaHouse = getBadhakaHouse(lagnaIdx >= 0 ? lagnaIdx : 0);
  const isBadhakaLord = ruledHouses.includes(badhakaHouse);

  const isFunctionalMalefic = (FUNCTIONAL_MALEFICS_BY_LAGNA[lagnaSign] || []).includes(planet);
  const isFunctionalBenefic = isYogakaraka || isLagnaLord || (FUNCTIONAL_BENEFICS_BY_LAGNA[lagnaSign] || []).includes(planet);

  let nature: ParashariLordship['nature'] = 'Neutral';
  if (isYogakaraka) {
    nature = 'Yogakaraka';
  } else if (isFunctionalBenefic) {
    nature = 'Benefic';
  } else if (isFunctionalMalefic) {
    nature = 'Malefic';
  } else if (isMaraka) {
    nature = 'Maraka';
  }

  let reason = '';
  if (isYogakaraka) {
    reason = `Yogakaraka for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')} uniting Kendra & Trikona).`;
  } else if (isLagnaLord) {
    reason = `Lagna Lord for ${lagnaSign} Lagna (rules house 1${ruledHouses.length > 1 ? ` and ${ruledHouses.filter(h => h !== 1).join(', ')}` : ''}).`;
  } else if (isPureDusthanaLord) {
    reason = `Pure Dusthana Lord for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')} without Trikona relief).`;
  } else if (isFunctionalMalefic) {
    reason = `Functional Malefic for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')}).`;
  } else if (isFunctionalBenefic) {
    reason = `Functional Benefic for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')}).`;
  } else if (isMaraka) {
    reason = `Maraka Lord for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')}).`;
  } else {
    reason = `Neutral / balanced functional influence for ${lagnaSign} Lagna (rules houses ${ruledHouses.join(', ')}).`;
  }

  return {
    planet,
    lagnaSign,
    ruledHouses,
    isFunctionalBenefic,
    isFunctionalMalefic,
    isYogakaraka,
    isMaraka,
    isPureDusthanaLord,
    isBadhakaLord,
    isLagnaLord,
    isTrikonaLord,
    isKendraLord,
    nature,
    reason
  };
}

export interface RemedySafetyEvaluation {
  isSafe: boolean;
  warningNote?: string;
  warningNoteHi?: string;
}

export function evaluateRemedySafety(
  rem: StructuredRemedyRecord,
  lagnaSign: ZodiacSign,
  kundali?: Partial<CompleteKundali>,
  moonNakIdxOverride?: number
): RemedySafetyEvaluation {
  if (rem.planet === 'GENERAL' || rem.planet === 'RAHU_KETU') {
    return { isSafe: true };
  }

  const lordship = evaluateParashariLordship(rem.planet as PlanetName, lagnaSign);

  if (rem.category === 'GEMSTONE') {
    // 1. Functional Malefic check
    // Classical lordship calculation: BPHS Ch. 34 defines functional malefic nature based on house lordship.
    // Gemstone eligibility policy: Withholding gemstones for functional malefics is a project Anukul-vada policy.
    if (lordship.isFunctionalMalefic) {
      const badhakaH = lordship.ruledHouses.find(h => [11, 9, 7].includes(h));
      const isBadhaka = lordship.isBadhakaLord && !lordship.isTrikonaLord && !lordship.isYogakaraka;
      const badhakaNoteEn = isBadhaka
        ? ` Badhaka consideration: rules Badhaka sthana (house ${badhakaH}) under Prasna Marga Ch. 15 v. 111 (horary rule; natal gemstone prohibition is unresolved).`
        : '';
      const badhakaNoteHi = isBadhaka
        ? ` बाधक विचार: प्रश्न मार्ग अ. १५ श्लो. १११ अनुसार बाधक भाव का स्वामी (जातक रत्न निषेध अनिर्णीत)।`
        : '';

      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION]${isBadhaka ? ' [UNCERTAINTY_BOUNDARY]' : ''} [RULE_CLASSICAL_LORDSHIP: BPHS Ch. 34] Contraindication: ${rem.planet} is a functional malefic for ${lagnaSign} Lagna under BPHS Ch. 34 lordship rules.${badhakaNoteEn} Wearing ${rem.name} is withheld under project Anukul-vada policy (BPHS Ch. 34 establishes functional planetary lordship, while gemstone prohibition is an interpretive remedial convention).`,
        warningNoteHi: `[परियोजना_विवेचना]${isBadhaka ? ' [अनिश्चितता_सीमा]' : ''} [शास्त्रीय_भावेश_नियम: BPHS अ. ३४] सावधानी: ${lagnaSign} लग्न हेतु ${rem.planet} अकारक ग्रह है।${badhakaNoteHi} परियोजना के अनुकूल-वाद नीति अनुसार रत्न धारण स्थगित है (BPHS अ. ३४ कार्यात्मक भावेश्वर निश्चित करता है, रत्न निषेध परियोजना विवेचना है)।`
      };
    }

    // 2. Pure Dusthana Lord check
    // Classical lordship calculation: BPHS Ch. 34 defines Dusthana lordship (6, 8, 12).
    // Gemstone eligibility policy: Withholding gemstones for pure Dusthana lords is a project Anukul-vada policy.
    if (lordship.isPureDusthanaLord) {
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [RULE_CLASSICAL_LORDSHIP: BPHS Ch. 34] Contraindication: ${rem.planet} rules pure Dusthana houses (${lordship.ruledHouses.join(', ')}) without Trikona lordship for ${lagnaSign} Lagna (BPHS Ch. 34). Gemstone wearing is withheld under project Anukul-vada policy (BPHS Ch. 34 defines Dusthana lordship, while gemstone prohibition is an interpretive remedial convention).`,
        warningNoteHi: `[परियोजना_विवेचना] [शास्त्रीय_भावेश_नियम: BPHS अ. ३४] सावधानी: ${lagnaSign} लग्न हेतु ${rem.planet} केवल त्रिक भावों (${lordship.ruledHouses.join(', ')}) का स्वामी है (BPHS अ. ३४)। परियोजना के अनुकूल-वाद नीति अनुसार रत्न धारण स्थगित है।`
      };
    }

    // 3. Badhaka Lord consideration (Prasna Marga Ch. 15 v. 111)
    if (lordship.isBadhakaLord && !lordship.isTrikonaLord && !lordship.isYogakaraka) {
      const badhakaH = lordship.ruledHouses.find(h => [11, 9, 7].includes(h)) || 'Badhaka';
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Badhaka consideration: ${rem.planet} rules Badhaka sthana (house ${badhakaH}) for ${lagnaSign} Lagna (Prasna Marga Ch. 15 v. 111 defines Badhaka sthana for horary inquiries). Classical authority on natal gemstone prohibition for Badhaka is unresolved in Parashari Jataka texts; personalized gemstone recommendation is withheld as a project interpretation pending qualified Acharya evaluation.`,
        warningNoteHi: `[परियोजना_विवेचना] [अनिश्चितता_सीमा] बाधक विचार: ${lagnaSign} लग्न हेतु ${rem.planet} बाधक भाव का स्वामी है (प्रश्न मार्ग अ. १५ श्लो. १११ प्रश्न विचार हेतु)। पाराशरी जातक ग्रंथों में बाधक ग्रह के रत्न निषेध का नियम अनिर्णीत होने के कारण व्यक्तिगत रत्न अनुशंसा स्थगित रखी गई है।`
      };
    }

    // 4. Classical Tara Bala evaluation (Muhurta Chintamani Ch. 1 v. 34)
    const moonNakIdx = moonNakIdxOverride !== undefined
      ? moonNakIdxOverride
      : kundali?.planets?.find(p => p.name === 'Moon')?.nakshatraIndex;

    if (moonNakIdx === undefined) {
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Personalized gemstone recommendation withheld: natal Moon nakshatra is unavailable, preventing mandatory classical Tara Bala calculation (Muhurta Chintamani Ch. 1 v. 34: "जन्म सम्पद्विपत्क्षेमः...").`,
        warningNoteHi: `[परियोजना_विवेचना] [अनिश्चितता_सीमा] व्यक्तिगत रत्न अनुशंसा स्थगित: जन्म नक्षत्र अनुपलब्ध होने के कारण शास्त्रीय तारा बल परीक्षण (मुहूर्त चिंतामणि अ. १ श्लो. ३४) संभव नहीं है।`
      };
    }

    const planetPos = kundali?.planets?.find(p => p.name === rem.planet);
    if (!planetPos || planetPos.nakshatraIndex === undefined) {
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Personalized gemstone recommendation withheld: astronomical coordinates for ${rem.planet} are unavailable for Tara Bala verification (Muhurta Chintamani Ch. 1 v. 34).`,
        warningNoteHi: `[परियोजना_विवेचना] [अनिश्चितता_सीमा] व्यक्तिगत रत्न अनुशंसा स्थगित: ${rem.planet} की खगोलीय स्थिति अनुपलब्ध होने के कारण तारा बल परीक्षण संभव नहीं है।`
      };
    }

    const tara = calculatePlanetTara(planetPos.nakshatraIndex, moonNakIdx);
    if (tara.index === 6 || tara.nameEn.includes('Naidhana') || tara.nameEn.includes('Vadha')) {
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Tara Bala consideration: ${rem.planet} occupies Naidhana (Vadha) Tara from natal Moon (Muhurta Chintamani Ch. 1 v. 34 defines electional Navatara). Extrapolation to natal gemstone prohibition is a project interpretation; personalized gemstone strengthening is withheld; no substitute remedy is synthesized.`,
        warningNoteHi: `[परियोजना_विवेचना] [अनिश्चितता_सीमा] तारा बल विचार: ${rem.planet} जन्म चंद्रमा से नैधन (वध) तारा में स्थित है (मुहूर्त चिंतामणि अ. १ श्लो. ३४)। रत्न संवर्धन अनुशंसा परियोजना विवेचनानुसार स्थगित रखी गई है; कोई कल्पित विकल्प नहीं बनाया गया है।`
      };
    }

    if (!tara.isSafe && !lordship.isLagnaLord) {
      return {
        isSafe: false,
        warningNote: `[PROJECT_INTERPRETATION] [UNCERTAINTY_BOUNDARY] Tara Bala consideration: ${rem.planet} occupies unfavorable ${tara.nameEn} Tara (Muhurta Chintamani Ch. 1 v. 34 defines electional Navatara). Extrapolation to natal gemstone prohibition is a project interpretation; personalized gemstone recommendation is withheld.`,
        warningNoteHi: `[परियोजना_विवेचना] [अनिश्चितता_सीमा] तारा बल विचार: ${rem.planet} चंद्रमा से प्रतिकूल ${tara.nameHi} में स्थित है। रत्न अनुशंसा स्थगित।`
      };
    }
  }

  return { isSafe: true };
}

export function assignRemedies(
  kundali: CompleteKundali,
  themeId: LifeThemeId,
  userEntitlement: 'BASIC' | 'PREMIUM' | 'ULTRA_PREMIUM' = 'BASIC',
  lang: Language = 'en'
): AssignedRemedyCandidate[] {
  const isHi = lang === 'hi';
  const targetKaraka = THEME_PRIMARY_KARAKAS[themeId] || 'Jupiter';
  const targetHouse = THEME_PRIMARY_HOUSES[themeId] || 1;
  const lagnaSign: ZodiacSign = (
    kundali.charts?.D1?.houses?.find(h => h.houseNumber === 1)?.sign ||
    kundali.houses?.[0]?.sign ||
    kundali.planets?.find(p => p.name === 'Ascendant')?.sign ||
    (kundali.birthDetails as any)?.ascendantSign ||
    'Aries'
  ) as ZodiacSign;
  const lagnaIdx = ZODIAC_SIGNS.indexOf(lagnaSign);

  // Dual-Pillar Evaluation: Derive Bhavesha (House Lord) for targetHouse
  let houseLord: PlanetName | undefined = undefined;
  const d1House = kundali.charts?.D1?.houses?.find(h => h.houseNumber === targetHouse);
  if (d1House && (d1House as any).houseLord) {
    houseLord = (d1House as any).houseLord as PlanetName;
  } else {
    const rootHouse = kundali.houses?.find(h => h.houseNumber === targetHouse) || kundali.houses?.[targetHouse - 1];
    if (rootHouse && (rootHouse as any).houseLord) {
      houseLord = (rootHouse as any).houseLord as PlanetName;
    } else if (rootHouse && (rootHouse as any).signLord) {
      houseLord = (rootHouse as any).signLord as PlanetName;
    }
  }

  if (!houseLord && lagnaIdx >= 0) {
    const houseSignIdx = (lagnaIdx + targetHouse - 1) % 12;
    const houseSignName = ZODIAC_SIGNS[houseSignIdx];
    houseLord = ZODIAC_LORDS[houseSignName] as PlanetName;
  }

  // Active Dasha synchronization
  const currentMd = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  const mdPlanet = currentMd?.planet as PlanetName | undefined;
  const currentAd = currentMd?.antardashas?.find(a => a.isCurrent);
  const adPlanet = currentAd?.planet as PlanetName | undefined;

  // Moon position for Tara Bala evaluation
  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  const moonNakIdx = moonPlanet?.nakshatraIndex;

  const candidates: AssignedRemedyCandidate[] = [];

  for (const rem of STRUCTURED_REMEDIES_BANK) {
    let relevanceScore = 5;
    let isSafe = true;
    let warningNote: string | undefined = undefined;
    let warningNoteHi: string | undefined = undefined;
    let whyThis = '';
    let whyThisHi = '';
    let whyNotAnother: string | undefined = undefined;
    let whyNotAnotherHi: string | undefined = undefined;

    const isTargetKaraka = rem.planet === targetKaraka;
    const isHouseLord = houseLord !== undefined && rem.planet === houseLord;
    const themeName = themeId.replace(/_/g, ' ');

    // Dual-Pillar Resolution: Independent evaluation of Naisargika Karaka (BPHS Ch. 3 v. 14–31) & Bhavesha (BPHS Ch. 11–24)
    // Classical Jyotisha establishes both pillars independently without forced hierarchical ranking or arbitrary affliction multipliers.
    if (isTargetKaraka && isHouseLord) {
      relevanceScore += 5;
      whyThis = `Specifically addresses ${rem.planet}, who is BOTH the natural significator (Naisargika Karaka, BPHS Ch. 3 v. 14–31) and the ${targetHouse}th House Lord (Bhavesha, BPHS Ch. 11–24) for ${themeName}.`;
      whyThisHi = `${themeName} के लिए ${rem.planet} स्वाभाविक कारक (नैसर्गिक कारक, BPHS अ. ३ श्लो. १४–३१) एवं ${targetHouse}वें भाव के स्वामी (भावेश, BPHS अ. ११–२४) दोनों हैं।`;
    } else if (isHouseLord) {
      relevanceScore += 4;
      whyThis = `Addresses ${houseLord}, ruler of the ${targetHouse}th house (Bhavesha, BPHS Ch. 11–24) governing ${themeName}. Independent Bhavesha evidence pillar.`;
      whyThisHi = `${themeName} के ${targetHouse}वें भाव के अधिपति (भावेश, BPHS अ. ११–२४) ${houseLord} की स्वतंत्र शास्त्रीय भाव-अधिपति मीमांसा।`;
    } else if (isTargetKaraka) {
      relevanceScore += 4;
      whyThis = `Specifically addresses ${targetKaraka}, primary natural significator (Naisargika Karaka, BPHS Ch. 3 v. 14–31) for ${themeName}. Independent Karaka evidence pillar.`;
      whyThisHi = `${themeName} के मुख्य प्राकृतिक कारक (नैसर्गिक कारक, BPHS अ. ३ श्लो. १४–३१) ग्रह ${targetKaraka} की स्वतंत्र शास्त्रीय कारक मीमांसा।`;
    } else if (rem.planet === 'GENERAL' || rem.planet === 'RAHU_KETU') {
      relevanceScore += 2;
      whyThis = `Provides universal supportive shield and removes underlying karmic obstacles.`;
      whyThisHi = `कल्याणकारी सुरक्षा कवच एवं बाधा निवारण में सहायक।`;
    }

    // Contextual Dasha Annotation (No arbitrary numerical score inflation)
    if (rem.planet !== 'GENERAL' && rem.planet !== 'RAHU_KETU') {
      if (mdPlanet && rem.planet === mdPlanet) {
        whyThis += ` [Active Mahadasha: ${mdPlanet} — temporal context]`;
        whyThisHi += ` [सक्रिय महादशा: ${mdPlanet} — समयानुसार संदर्भ]`;
      } else if (adPlanet && rem.planet === adPlanet) {
        whyThis += ` [Active Antardasha: ${adPlanet} — temporal context]`;
        whyThisHi += ` [सक्रिय अंतर्दशा: ${adPlanet} — समयानुसार संदर्भ]`;
      }
    }

    // Parashari Lordship & Practice-Specific Contraindication Checks
    const safety = evaluateRemedySafety(rem, lagnaSign, kundali, moonNakIdx);
    if (!safety.isSafe) {
      isSafe = false;
      relevanceScore -= 8;
      warningNote = safety.warningNote;
      warningNoteHi = safety.warningNoteHi;
      whyNotAnother = undefined;
      whyNotAnotherHi = undefined;
    } else {
      warningNote = safety.warningNote;
      warningNoteHi = safety.warningNoteHi;
      if (rem.category === 'GEMSTONE') {
        whyThis += ' [PROJECT_INTERPRETATION: Gemstone strengthening eligible under project Anukul-vada policy]';
        whyThisHi += ' [परियोजना_विवेचना: अनुकूल-वाद नीति अनुसार रत्न संवर्धन योग्य]';
      } else if (rem.planet !== 'GENERAL' && rem.planet !== 'RAHU_KETU') {
        const lordship = evaluateParashariLordship(rem.planet as PlanetName, lagnaSign);
        if (lordship.isFunctionalMalefic || lordship.isPureDusthanaLord) {
          if (rem.category === 'DANA_CHARITY' || rem.purpose === 'PACIFYING' || rem.category === 'SEVA_SERVICE') {
            whyThis += ` [Pacification of ${lordship.nature} influence]`;
            whyThisHi += ` [${rem.planet} के अशुभ प्रभाव की शांति हेतु]`;
          }
        }
      }
    }

    // Rank assignment:
    // Both Naisargika Karaka and Bhavesha (House Lord) are evaluated as independent PRIMARY candidates.
    // Unresolved affliction prioritization formulas are not assumed; both retain PRIMARY rank side-by-side.
    let rank: AssignedRemedyCandidate['rank'] = 'OPTIONAL_SUPPORT';
    if (isTargetKaraka || isHouseLord) {
      rank = 'PRIMARY';
    } else if (relevanceScore >= 7) {
      rank = 'SECONDARY';
    }

    // Assign candidates if safe and relevance meets threshold
    if (isSafe && relevanceScore >= 4) {
      candidates.push({
        remedy: rem,
        rank,
        relevanceScore,
        whyThisRemedy: whyThis || rem.explanation,
        whyThisRemedyHi: whyThisHi || rem.explanationHi,
        whyNotAnotherRemedy: whyNotAnother,
        whyNotAnotherRemedyHi: whyNotAnotherHi,
        contraindicationCheck: {
          isSafe: true,
          warningNote,
          warningNoteHi
        }
      });
    }
  }

  // Balanced Dual-Pillar Candidate Selection:
  // Ensure both Naisargika Karaka (BPHS Ch. 3) and Bhavesha (BPHS Ch. 11–24) are represented equally without forced ranking or starvation
  if (houseLord && houseLord !== targetKaraka) {
    const karakaCandidates = candidates.filter(c => c.remedy.planet === targetKaraka);
    const houseLordCandidates = candidates.filter(c => c.remedy.planet === houseLord);
    const otherCandidates = candidates.filter(c => c.remedy.planet !== targetKaraka && c.remedy.planet !== houseLord);

    karakaCandidates.sort((a, b) => b.relevanceScore !== a.relevanceScore ? b.relevanceScore - a.relevanceScore : b.remedy.priorityScore - a.remedy.priorityScore);
    houseLordCandidates.sort((a, b) => b.relevanceScore !== a.relevanceScore ? b.relevanceScore - a.relevanceScore : b.remedy.priorityScore - a.remedy.priorityScore);
    otherCandidates.sort((a, b) => b.relevanceScore !== a.relevanceScore ? b.relevanceScore - a.relevanceScore : b.remedy.priorityScore - a.remedy.priorityScore);

    // Balanced representation: Karaka (top 3) and Bhavesha (top 3) without forced ranking
    const selected: AssignedRemedyCandidate[] = [
      ...karakaCandidates.slice(0, 3),
      ...houseLordCandidates.slice(0, 3),
      ...karakaCandidates.slice(3),
      ...houseLordCandidates.slice(3),
      ...otherCandidates
    ];
    return selected.slice(0, 6);
  }

  // Sort candidates by priority & relevance for single-pillar / convergence cases
  candidates.sort((a, b) => b.relevanceScore !== a.relevanceScore ? b.relevanceScore - a.relevanceScore : b.remedy.priorityScore - a.remedy.priorityScore);

  return candidates.slice(0, 6);
}
